import { MessageDirection, MessageResponse, MessageType } from "client/openapi";
import { isOwnEmailCopy } from "components/MessageDeliveryTag";
import { Fragment, useState } from "react";

type Named = { id: number; first_name: string; last_name: string };

/**
 * One name on the line. A student, parent or tutor links to their profile;
 * an unknown sender's number or address opens Link or add; the
 * organization and its staff are plain.
 */
export type Party = {
  key: string;
  label: string;
  href?: string;
  onClick?: () => void;
  title?: string;
};

const fullName = (person: { first_name: string; last_name: string }) =>
  `${person.first_name} ${person.last_name}`;

const people = (
  kind: "parent" | "student" | "tutor",
  list?: Named[] | null
): Party[] =>
  (list ?? []).map((person) => ({
    key: `${kind}:${person.id}`,
    label: fullName(person),
    href: `/manage/${kind}s/${person.id}`,
  }));

const unique = (parties: Party[]) =>
  parties.filter(
    (party, i) => parties.findIndex((p) => p.key === party.key) === i
  );

/**
 * Who sent a message: the people a received one matched (or the number or
 * address of a sender who matches no one), or the staff member who sent it.
 */
export function sentBy(
  message: MessageResponse,
  organizationName: string,
  onUnknownSender?: () => void
): Party[] {
  if (message.direction === MessageDirection.INBOUND) {
    const matched = unique([
      ...people("parent", message.matched_parents),
      ...people("student", message.matched_students),
      ...people("tutor", message.matched_tutors),
    ]);
    if (matched.length) return matched;
    return [
      {
        key: "unknown",
        label: message.sender_phone || message.sender_email || "Unknown",
        onClick: onUnknownSender,
        title: onUnknownSender
          ? "Link to a student, parent or tutor, or add them"
          : undefined,
      },
    ];
  }
  return [
    {
      key: "sender",
      label: message.sender
        ? fullName(message.sender)
        : message.sender_email || organizationName,
      title: isOwnEmailCopy(message)
        ? `Sent from ${message.sender_email}, not through Tadpole`
        : undefined,
    },
  ];
}

const INVITES: MessageType[] = [
  MessageType.STUDENT_INVITE,
  MessageType.PARENT_INVITE,
  MessageType.TUTOR_INVITE,
];

/**
 * Everyone who received a message, from its real recipient rows: the people
 * a sent one went to, or for a received one the organization and the people
 * in its To and Cc. An invite is addressed to the person and to their login,
 * so it names the person once.
 */
export function receivedBy(
  message: MessageResponse,
  organizationName: string
): Party[] {
  const persons = [
    ...people("parent", message.parent_recipients),
    ...people("student", message.student_recipients),
    ...people("tutor", message.tutor_recipients),
  ];
  const accounts =
    INVITES.includes(message.m_type) && persons.length
      ? []
      : (message.account_recipients ?? []).map((account) => ({
          key: `account:${account.id}`,
          label: fullName(account),
        }));
  const recipients = [...persons, ...accounts];
  return unique(
    message.direction === MessageDirection.INBOUND
      ? [{ key: "organization", label: organizationName }, ...recipients]
      : recipients
  );
}

const linkClass =
  "text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-800 hover:decoration-gray-500";

function Name({ party }: { party: Party }) {
  if (party.href) {
    return (
      <a
        href={party.href}
        target="_blank"
        rel="noreferrer"
        className={linkClass}
        title={party.title}
      >
        {party.label}
      </a>
    );
  }
  if (party.onClick) {
    return (
      <button
        type="button"
        className={linkClass}
        title={party.title}
        onClick={party.onClick}
      >
        {party.label}
      </button>
    );
  }
  return (
    <span className="text-gray-600" title={party.title}>
      {party.label}
    </span>
  );
}

/** "Ana Lopez", "Ana Lopez and Ben Lopez", or "Ana Lopez and 2 others" until opened. */
function Names({ parties }: { parties: Party[] }) {
  const [open, setOpen] = useState(false);
  const shown = open || parties.length <= 2 ? parties : parties.slice(0, 1);
  const rest = parties.length - shown.length;
  return (
    <>
      {shown.map((party, i) => (
        <Fragment key={party.key}>
          {i > 0 && (i === shown.length - 1 && !rest ? " and " : ", ")}
          <Name party={party} />
        </Fragment>
      ))}
      {rest > 0 && (
        <>
          {" and "}
          <button
            type="button"
            className="text-gray-600 underline decoration-dotted underline-offset-2 hover:text-gray-800"
            aria-expanded={false}
            onClick={() => setOpen(true)}
          >
            {rest} others
          </button>
        </>
      )}
    </>
  );
}

/**
 * One small gray line on each message, Gmail style: "From Inas Almasry · To
 * Apollo Tutoring". People's names link to their profiles (a new tab); a
 * long list shows its first name and "N others", which opens the rest. The
 * whole line shows on hover.
 */
export default function FromToLine({
  message,
  organizationName,
  onUnknownSender,
}: {
  message: MessageResponse;
  organizationName: string;
  /** Link or add the sender of a received message who matches no one. */
  onUnknownSender?: () => void;
}) {
  const from = sentBy(message, organizationName, onUnknownSender);
  const to = receivedBy(message, organizationName);
  const all = (parties: Party[]) => parties.map((p) => p.label).join(", ");

  return (
    <div
      className="text-xs text-gray-500 break-words"
      title={`From ${all(from)}${to.length ? ` · To ${all(to)}` : ""}`}
    >
      From <Names parties={from} />
      {to.length > 0 && (
        <>
          {" · To "}
          <Names parties={to} />
        </>
      )}
    </div>
  );
}
